import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Droplets, ArrowRightLeft } from "lucide-react";
import { Section, SectionHead, SourceLink } from "@/components/common";

export default function WaterCooling() {
  const [methods, setMethods] = useState([]);
  const [explainers, setExplainers] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/cooling-methods").then((r) => { setMethods(r.data); setActive(r.data[0]); });
    api.get("/explainers").then((r) => setExplainers(r.data.filter((e) => ["Water", "Cooling"].includes(e.topic))));
  }, []);

  return (
    <Section className="py-12 sm:py-16">
      <SectionHead eyebrow="Water & Cooling Guide" title="How data centers stay cool — and how much water that takes"
        desc="Plain-language explanations of common cooling approaches. We distinguish water withdrawn from water consumed and never fill missing water-use data with invented numbers." />

      <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 px-5 py-4 mb-8 flex items-start gap-3">
        <ArrowRightLeft size={18} className="text-[#3B82F6] mt-0.5 shrink-0" />
        <p className="text-blue-100/90 text-sm"><strong className="text-white">Withdrawn vs. consumed:</strong> Water <em>withdrawn</em> is taken from a source and largely returned. Water <em>consumed</em> is evaporated or otherwise not returned. Figures should always state gallons/liters, the time period, location, and measurement boundary.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-2">
          {methods.map((m) => (
            <button key={m.id} data-testid={`cooling-method-${m.slug}`} onClick={() => setActive(m)}
              className={`card p-4 w-full text-left ${active?.id === m.id ? "border-blue-500/60 glow-blue" : ""}`}>
              <div className="flex items-center justify-between">
                <h4 className="font-display font-semibold text-white">{m.name}</h4>
                <Droplets size={15} className="text-[#3B82F6]" />
              </div>
              <p className="text-xs text-slate-400 mt-1">{m.water_profile}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3">
          {active && (
            <div className="card p-6 sm:p-8" data-testid="cooling-detail">
              <h3 className="font-display font-bold text-2xl text-white">{active.name}</h3>
              <p className="text-slate-300 mt-3 leading-relaxed">{active.description}</p>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-[#0B0F17] rounded-lg p-4 border border-white/5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Water withdrawn</p>
                  <p className="text-white">{active.water_withdrawn}</p>
                </div>
                <div className="bg-[#0B0F17] rounded-lg p-4 border border-white/5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Water consumed</p>
                  <p className="text-white">{active.water_consumed}</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 mt-6">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#84CC16] mb-2">Strengths</p>
                  <ul className="space-y-1.5 text-sm text-slate-300">{(active.pros || []).map((p, i) => <li key={i}>• {p}</li>)}</ul>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-2">Trade-offs</p>
                  <ul className="space-y-1.5 text-sm text-slate-300">{(active.cons || []).map((p, i) => <li key={i}>• {p}</li>)}</ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        {explainers.map((e) => (
          <div key={e.id} className="card p-6" data-testid={`wc-explainer-${e.slug}`}>
            <p className="eyebrow mb-2">{e.topic}</p>
            <h3 className="font-display font-bold text-xl text-white">{e.title}</h3>
            <p className="text-slate-300 text-sm mt-2 leading-relaxed">{e.body}</p>
            <div className="mt-3 space-y-1">{(e.sources || []).map((s, i) => <SourceLink key={i} source={s} />)}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}
